import React, { useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { fetchWithAuth } from '../../services/api';
import { toast } from 'react-toastify';

const ReportDownloader = forwardRef(({ selectedDay, selectedMonth, setLoadingArr, setMonthReportDone, station }, ref) => {

  const downloadExcelReport = useCallback(async (type, datum, setLoadingArr, key, station, userId) => {
    setLoadingArr(prev => ({ ...prev, [key]: true }));
    try {
      let urlLink = '/api/reports/excel';
      if (userId) urlLink += `?userId=${userId}`;
      const response = await fetchWithAuth(urlLink, {
        method: 'POST',
        headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
        body: JSON.stringify({ type, datum, station })
      });

      if (!response.ok) {
        toast.error('❌ Fel vid nedladdning av rapport');
        return;
      }

      let fileName = datum.replace(/[/\\?%*:|"<> ]/g, '_') + '.xlsx';
      const contentDisposition = response.headers.get('content-disposition');
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) fileName = fileNameMatch[1].replace(/['"]/g, '');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      toast.error(error.msg || `❌ Fel vid nedladdning av ${type}-rapport`);
    } finally {
      setLoadingArr(prev => ({ ...prev, [key]: false }));
    }
  }, []);

  const createDayExcelReport = useCallback((key, userId) => {
    downloadExcelReport('dag', selectedDay, setLoadingArr, key, station, userId);
  }, [selectedDay, setLoadingArr, downloadExcelReport, station]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'downloadDagExcel') {
        createDayExcelReport('btn1', undefined);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [createDayExcelReport]);

  const createHtmlReport = useCallback(async (key, userId) => {
    setLoadingArr(prev => ({ ...prev, [key]: true }));

    const newWindow = window.open('', '_blank');
    if (!newWindow) {
      toast.error('❌ Popup blockerad av webbläsaren. Tillåt popups för denna sida.');
      setLoadingArr(prev => ({ ...prev, [key]: false }));
      return;
    }

    newWindow.document.write(`
      <!DOCTYPE html><html lang="sv">
      <head><meta charset="utf-8"><title>Laddar...</title></head>
      <body style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:sans-serif;">
        <div style="text-align:center">
          <div style="font-size:24px;margin-bottom:16px;">⏳</div>
          <p>Genererar dagbesked...</p>
        </div>
      </body></html>
    `);

    try {
      let urlLink = '/api/reports/html';
      if (userId) urlLink += `?userId=${userId}`;
      const response = await fetchWithAuth(urlLink, {
        method: 'POST',
        headers: { 'Accept': 'text/html' },
        body: JSON.stringify({
          location: window.location.origin,
          type: 'dag',
          datum: selectedDay,
          station: station
        })
      });

      if (!response.ok) {
        newWindow.close();
        toast.error('❌ Fel vid generering av HTML-rapport');
        return;
      }

      const html = await response.text();

      const downloadButton = `<div id="download-bar" style="position:fixed;top:10px;right:10px;z-index:9999;">
        <button id="btnDownloadExcel"
          style="background:#2563eb;color:white;padding:8px 16px;border:none;border-radius:6px;
                 cursor:pointer;font-size:14px;font-weight:bold;box-shadow:0 2px 4px rgba(0,0,0,0.2);">
          &#x1F4E5; Ladda ner Excel
        </button>
      </div>`;

      const htmlWithButton = html.replace('</body>', downloadButton + '</body>');
      newWindow.document.open();
      newWindow.document.write(htmlWithButton);
      newWindow.document.close();

      newWindow.document.getElementById('btnDownloadExcel').addEventListener('click', () => {
        window.postMessage({ type: 'downloadDagExcel' }, '*');
      });
    } catch (error) {
      newWindow.close();
      console.error('Fel vid generering av HTML-rapport:', error);
      throw error;
    } finally {
      setLoadingArr(prev => ({ ...prev, [key]: false }));
    }
  }, [selectedDay, station]);

  const createExcelReport = useCallback(async (key, userId) => {
    setLoadingArr(prev => ({ ...prev, [key]: true }));
    try {
      let urlLink = '/api/reports/excel';
      if (userId) urlLink += `?userId=${userId}`;
      const response = await fetchWithAuth(urlLink, {
        method: 'POST',
        headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
        body: JSON.stringify({ type: 'manad', datum: selectedMonth, station: station })
      });

      if (!response.ok) {
        toast.error('❌ Fel vid nedladdning av månadsrapport');
        return;
      }

      let fileName = selectedMonth.replace(/[/\\?%*:|"<> ]/g, '_') + '.xlsx';
      const contentDisposition = response.headers.get('content-disposition');
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) fileName = fileNameMatch[1].replace(/['"]/g, '');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      toast.error(error.msg || '❌ Fel vid nedladdning av månadsrapport');
    } finally {
      setLoadingArr(prev => ({ ...prev, [key]: false }));
      setMonthReportDone(true);
    }
  }, [selectedMonth, station, setMonthReportDone]);

  useImperativeHandle(ref, () => ({
    createHtmlReport,
    createExcelReport,
  }));

  return <></>;
});

ReportDownloader.displayName = 'ReportDownloader';

export default ReportDownloader;
